import React, {FormEvent, ReactNode} from "react";
import {UserPermissionEnum} from "../../../../utils/types/user";

import {
  TemporaryHideChart,
  Props as TemporaryHideChartProps,
} from "../../TemporaryHideChart";
import {PERMISSION_BLOCKS} from "../../permissions.helpers";
import {
  formatBulkCategoryBadgeLabel,
  getBulkAssignmentState,
  getBulkCategoryBadgeVariant,
} from "./bulkPermissions.helpers";
import {
  BulkPermissionCategoryCard,
  BulkPermissionCategoryItem,
  BulkPermissionRow,
} from "./BulkPermissionCategoryCard";

type Props = Readonly<{
  label: ReactNode;
  permissions: ReadonlyArray<{
    value: UserPermissionEnum;
    label: string;
  }>;
  userIds: ReadonlyArray<string>;
  hasPermission: (userId: string, permission: UserPermissionEnum) => boolean;
  isPermissionPending?: (permission: UserPermissionEnum) => boolean;
  onTogglePermission: (
    permission: UserPermissionEnum,
    grantToAll: boolean,
  ) => void;
  onToggleCategory: (
    permissions: ReadonlyArray<UserPermissionEnum>,
    grantToAll: boolean,
  ) => void;
  TemporaryHideChart: (props: TemporaryHideChartProps) => JSX.Element;
}>;

export const BulkPermissionsMacroCheckbox = ({
  label,
  permissions,
  userIds,
  hasPermission,
  isPermissionPending,
  onTogglePermission,
  onToggleCategory,
  TemporaryHideChart: TemporaryHideChartComponent,
}: Props) => {
  const permissionValues = permissions.map(({value}) => value);
  const permissionStates = permissions.map(({value}) =>
    getBulkAssignmentState(userIds, userId => hasPermission(userId, value)),
  );
  const someChecked = permissionStates.some(
    state => state.assignedCount > 0,
  );
  const everyChecked = permissionStates.every(
    state => state.variant === "all",
  );

  const onGroupChange = (event: FormEvent<HTMLInputElement>) =>
    onToggleCategory(permissionValues, event.currentTarget.checked);

  const onOneChange =
    (value: UserPermissionEnum) => (event: FormEvent<HTMLInputElement>) =>
      onTogglePermission(value, event.currentTarget.checked);

  const categoryPending = permissionValues.some(
    value => isPermissionPending?.(value) ?? false,
  );

  return (
    <BulkPermissionCategoryCard
      label={label}
      badgeLabel={formatBulkCategoryBadgeLabel(permissionStates)}
      badgeVariant={getBulkCategoryBadgeVariant(permissionStates)}
      someChecked={someChecked}
      everyChecked={everyChecked}
      isPending={categoryPending}
      onGroupChange={onGroupChange}
    >
      {permissions.map(({value, label: itemLabel}) => (
        <BulkPermissionCategoryItem key={value}>
          <TemporaryHideChartComponent chartIds={PERMISSION_BLOCKS[value] || []}>
            <BulkPermissionRow
              label={itemLabel}
              userIds={userIds}
              hasAssignment={userId => hasPermission(userId, value)}
              isPending={isPermissionPending?.(value) ?? false}
              onChange={onOneChange(value)}
            />
          </TemporaryHideChartComponent>
        </BulkPermissionCategoryItem>
      ))}
    </BulkPermissionCategoryCard>
  );
};

BulkPermissionsMacroCheckbox.defaultProps = {
  TemporaryHideChart,
};
